import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useMergeState from "../../../shared/hooks/useMergeState";
import { api } from "../../../shared/hooks/api";
import { AccountingAccount } from "../../../data/entities";
import { error, success } from "../../../utils/toast";
import { AccountingAccountWrapper } from "../create/Styled";
import BackButton from "../../../shared/components/BackButton";
import Title from "./Title";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import FormContainer from "../create/Content/FormContainer";

const AccountingAccountDetail = () => {
    const { accountingAccountId } = useParams<{
        accountingAccountId: string;
    }>();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [accountData, setAccountData] = useMergeState({});
    const [{ isLoading: isUpdating }, updateAccount] = api.patch(
        `/accounting/accounts/${accountingAccountId}/update/`
    );
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [parentAccounts, setParentAccounts] = useState<AccountingAccount[]>(
        []
    );
    const isParent = !Boolean(accountData.parent);

    // Fetch account details
    const [{ data: accountDetail, isLoading: isLoadingDetail }] = api.get(
        `/accounting/accounts/${accountingAccountId}/`
    );

    // Fetch parent accounts from the main list
    const [{ data: parentAccountsData }] = api.get("/accounting/accounts/", {
        page_size: 1000,
    });

    useEffect(() => {
        if (accountDetail) {
            setAccountData({
                ...accountDetail,
                status: accountDetail.status === "ACTIVE",
                parent: accountDetail.parent?.id || null,
            });
        }
    }, [accountDetail, setAccountData]);

    useEffect(() => {
        if (parentAccountsData?.results) {
            // Allow any account to be selected as parent, exclude current account for UX
            const candidates = parentAccountsData.results.filter(
                (account: AccountingAccount) => account.id !== accountingAccountId
            );
            setParentAccounts(candidates);
        }
    }, [parentAccountsData, accountingAccountId]);

    // Mark as modified when accountData changes
    useEffect(() => {
        if (accountData.id) {
            setIsModified(true);
        }
    }, [accountData]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback(
        (path: string) => {
            if (isModified) {
                setNavigationPath(path);
                setShowUnsavedDialog(true);
                return false;
            }

            navigate(path);
            return true;
        },
        [isModified, navigate]
    );

    const handleBackClick = () => {
        confirmNavigation("/accounting-accounts/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleSubmit = async () => {
        const requestBody: any = {
            code: accountData.code,
            name: accountData.name,
            english_translation: accountData.english_translation,
            note: accountData.note,
            status: accountData.status ? "ACTIVE" : "INACTIVE",
            parent: accountData.parent,
            // Always send type so user can override suggested type even when account has a parent
            type: accountData.type,
        };

        await updateAccount(requestBody)
            .then((data: any) => {
                success("Thành Công", "Cập nhật tài khoản kế toán thành công");
                setIsModified(false);
                return true;
            })
            .catch((err: any) => {
                error("Lỗi cập nhật tài khoản kế toán", err);
                return false;
            });
    };

    const handleChangeAccountData = (updates: any) => {
        setIsModified(true);
        setAccountData(updates);
    };

    if (isLoadingDetail) {
        return <div>Loading...</div>;
    }

    return (
        <AccountingAccountWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title="Chỉnh sửa tài khoản kế toán"
                setValidated={setValidated}
                submitForm={handleSubmit}
                isLoading={isUpdating}
            />
            <FormContainer
                validated={validated}
                accountData={accountData}
                mergeAccount={handleChangeAccountData}
                parentAccounts={parentAccounts}
                isParent={accountData.parent === null}
            />

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </AccountingAccountWrapper>
    );
};

export default AccountingAccountDetail;
